import { baseApi } from 'services/api';
import { API_TAG_TYPES, API_VERSION } from 'constants/common';
import { getUrlSearchParams } from 'utils/apiUtils';
import {
  CreateServiceAreaPayload,
  CreateServiceAreaResponse,
  GetServiceAreaColorCodesResponse,
  GetServiceAreaItem,
  GetServiceAreasParams,
  PaginatedServiceAreaResponse,
  UpdateServiceAreaAssigneesPayload,
  UpdateServiceAreaPayload,
  UpdateServiceAreaStatusPayload
} from './serviceAreas.model';

const ServiceAreasApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getServiceAreas: builder.query<PaginatedServiceAreaResponse, { teamId: string; params: GetServiceAreasParams }>({
      query: ({ teamId, params }) => {
        let url = `${API_VERSION.V1}/teams/${teamId}/service-areas`;
        const searchParams = getUrlSearchParams({ ...params });
        if (searchParams) {
          url += `?${searchParams}`;
        }
        return {
          url,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.SERVICE_AREAS],
      extraOptions: {
        failure: 'Failed to fetch service areas',
        showToast: false
      }
    }),
    createServiceArea: builder.mutation<CreateServiceAreaResponse, CreateServiceAreaPayload>({
      query: ({ teamId, ...payload }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/service-areas`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.SERVICE_AREAS],
      extraOptions: {
        failure: 'Failed to create service area',
        success: 'Service area created successfully',
        showToast: true
      }
    }),
    getServiceAreaById: builder.query<GetServiceAreaItem, { teamId: string; serviceAreaId: string }>({
      query: ({ teamId, serviceAreaId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/service-areas/${serviceAreaId}`,
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.SERVICE_AREAS],
      extraOptions: {
        failure: 'Failed to fetch service area',
        showToast: true
      }
    }),
    updateServiceArea: builder.mutation<
      { code: number; message: string; details: string },
      { teamId: string; serviceAreaId: string; payload: UpdateServiceAreaPayload }
    >({
      query: ({ teamId, serviceAreaId, payload }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/service-areas/${serviceAreaId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.SERVICE_AREAS],
      extraOptions: {
        failure: 'Failed to update service area',
        success: 'Service area updated successfully',
        showToast: true
      }
    }),
    updateServiceAreaStatus: builder.mutation<
      { code: number; message: string; details: string },
      { teamId: string; serviceAreaId: string; payload: UpdateServiceAreaStatusPayload }
    >({
      query: ({ teamId, serviceAreaId, payload }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/service-areas/${serviceAreaId}/status`,
        method: 'PATCH',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.SERVICE_AREAS],
      extraOptions: {
        failure: 'Failed to update service area status',
        success: 'Service area status updated successfully',
        showToast: true
      }
    }),
    updateServiceAreaAssignees: builder.mutation<
      { code: number; message: string; details: string },
      { teamId: string; serviceAreaId: string; payload: UpdateServiceAreaAssigneesPayload }
    >({
      query: ({ teamId, serviceAreaId, payload }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/service-areas/${serviceAreaId}/assignees`,
        method: 'PATCH',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.SERVICE_AREAS],
      extraOptions: {
        failure: 'Failed to update service area assignees',
        success: 'Service area assignees updated successfully',
        showToast: true
      }
    }),
    getServiceAreaColorCodes: builder.query<GetServiceAreaColorCodesResponse, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/service-areas/color-codes`,
        method: 'GET'
      })
    })
  })
});

export const {
  useGetServiceAreasQuery,
  useCreateServiceAreaMutation,
  useLazyGetServiceAreasQuery,
  useGetServiceAreaByIdQuery,
  useLazyGetServiceAreaByIdQuery,
  useUpdateServiceAreaMutation,
  useUpdateServiceAreaStatusMutation,
  useUpdateServiceAreaAssigneesMutation,
  useGetServiceAreaColorCodesQuery
} = ServiceAreasApi;
